import { useState, useEffect } from "react";
import useApiCall from "../../components/api/useApiCall";
import { config, LINKS } from "configs/constants";
import { trackPageView, trackObjectClicked } from '../../configs/mixpanel';
import blueTick from "../../assets/media/migrate/blue-tick.png";
import cpu from "../../assets/media/migrate/cpu.png";
import heroMobile from "../../assets/media/migrate/hero-migrate-mobile.png";
import heroImage from "../../assets/media/migrate/hero-migrate.png";
import os from "../../assets/media/migrate/os.png";
import ram from "../../assets/media/migrate/ram.png";
import storage from "../../assets/media/migrate/storage.png";
import tick from "../../assets/media/migrate/tick.png";
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Cookies from 'js-cookie'

const TABS = [
  { id: 1, label: 'Intro' },
  { id: 2, label: 'Requirements' },
  { id: 3, label: 'Next Step' },
];

const BUTTON_CONFIG = {
  1: { label: 'Continue',                stepClass: 'step1' },
  2: { label: 'I meet the requirements', stepClass: 'step2' },
  3: { label: 'Lets Begin',              stepClass: 'step3' },
};

const MIGRATION_MAP = {
  'BN_IS_X9_1_1':  'ONE_ESSENTIAL_1_1',
  'BN_IS_X9_3_1':  'ONE_ESSENTIAL_3_1',
  'BN_IS_X9_5_1':  'ONE_ESSENTIAL_5_1',
  'BN_IS_X9_1_2':  'ONE_ESSENTIAL_1_2',
  'BN_IS_X9_3_2':  'ONE_ESSENTIAL_3_2',
  'BN_IS_X9_5_2':  'ONE_ESSENTIAL_5_2',
  'BN_MPB_X9_1_1': 'ONE_ADVANCED_1_1',
  'BN_MPB_X9_3_1': 'ONE_ADVANCED_3_1',
  'BN_MPB_X9_5_1': 'ONE_ADVANCED_5_1',
  'BN_MPB_X9_1_2': 'ONE_ADVANCED_1_2',
  'BN_MPB_X9_3_2': 'ONE_ADVANCED_3_2',
  'BN_MPB_X9_5_2': 'ONE_ADVANCED_5_2',
};

const REQUIREMENTS = [
  { icon: os,      label: 'OS',      value: 'macOS 12.4 or later' },
  { icon: cpu,     label: 'CPU',     value: 'Intel or Apple Silicon processor' },
  { icon: ram,     label: 'RAM',     value: 'at least 8 GB of memory (16 GB recommended for best performance)' },
  { icon: storage, label: 'Storage', value: '2 GB of available disk space' },
]

const NEXT_STEP_ITEMS = [
  'Your current Intego antivirus app will be automatically replaced by Intego ONE.',
  'You will continue to have access to the same features included in your plan (Antivirus, Firewall, SmartClean, VPN, if applicable).',
  <>You will <strong>not</strong> be charged extra - your billing amount and payment method stay the same.</>,
  'After installation, you can manage your plan and settings from your Intego ONE account at any time.',
]

const TAB_TRACKING = {
  1: { continue: 'intro_continue',        back: 'intro_back'        },
  2: { continue: 'requirements_met',      back: 'requirements_back' },
  3: { continue: 'migration_steps_completed', back: 'final_step_back' },
}

const getRedirectionUrl = () => {
  const host = window.location.host;
  const email = localStorage.getItem('e');
  const token = localStorage.getItem('h');
  const suffixUrl = `/migrate-route?email=${email}&token=${token}`

  let baseUrl = LINKS.newAccountUrl.prod;
    if(host.includes("staging")){
      baseUrl = LINKS.newAccountUrl.stage;
    }
    if (host.includes("localhost")) {
      baseUrl = `http://localhost:3000`;
    }

      return baseUrl + suffixUrl;
};

function StepIntro() {
  return (
    <div>
      <h5 className="migrate-title">Intego is happy to introduce its new antivirus app, Intego ONE</h5>
      <div className="migrate-desc-block">
        <p className="migrate-desc">
          Intego ONE is our new antivirus app for Mac, designed to replace your current Intego
          security software with a faster, more unified experience.
        </p>
        <p className="migrate-desc">
          If you already have an Intego antivirus product, Intego ONE is included at no additional
          cost and gives you the same level of protection you are used to - now in a single app.
        </p>
      </div>
      <div className="migrate-divider" />
    </div>
  );
}

function StepRequirements() {
  return (
    <div>
      <h1 className="migrate-title step2">Minimum system requirements</h1>
      <p className="migrate-subtitle">To install Intego ONE, your Mac must meet the following requirements:</p>
      <div className="migrate-req-grid">
        {REQUIREMENTS.map(({ icon, label, value }) => (
          <div key={label} className="migrate-req-item">
            <span className="migrate-req-icon"><img src={icon} alt={`${label} icon`} /></span>
            <div className="migrate-req-text">
              <div className="migrate-req-label">{label}</div>
              <div className="migrate-req-value">{value}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="migrate-spacer" />
      <div className="migrate-divider" />
    </div>
  )
}

function StepNextStep({ checked, onCheck }) {
  return (
    <div>
      <h1 className="migrate-title">What will happen when you install Intego ONE</h1>
      <ul className="migrate-checklist">
        {NEXT_STEP_ITEMS.map((item, i) => (
          <li key={i}>
            <img src={tick} alt="" className="migrate-tick" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <div className="migrate-step3-divider" />
      <label className="migrate-checkbox-mobile">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            if (e.target.checked) trackObjectClicked('final_step_check')
            onCheck(e.target.checked)
          }}
        />
        <span>I have read and understand the changes above.</span>
      </label>
    </div>
  )
}

function MigrateModal() {
  const [activeTab, setActiveTab] = useState(1)
  const [agreed, setAgreed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [{ isLoaded, data }] = useApiCall('subscriptions')

  const subscriptions = Array.isArray(data?.message?.subscriptions) ? data.message.subscriptions : []
  const bnSub = subscriptions.find(
    (s) => MIGRATION_MAP[s.sku] && s.status === 'active' && s.isRecurring && !(s.assignedDevices?.windows > 0)
  ) || null

  const { label, stepClass } = BUTTON_CONFIG[activeTab]
  const continueDisabled = activeTab === 3 && (!agreed || isLoading)

  useEffect(() => {
    trackPageView('migrate')
  }, [])

  const handleContinue = async () => {
    trackObjectClicked(TAB_TRACKING[activeTab].continue)

    if (activeTab < 3) return setActiveTab((t) => t + 1)

    if (!isLoaded || !bnSub) {
      console.error('Subscription data not ready')
      return
    }

    setIsLoading(true)
    try {
      const token = localStorage.getItem('h')
      const url = `${config.urls.ACCOUNT_URL}/api/user/subscription-upgrade`
        + `?api_token=${token}`
        + `&subscription=${bnSub.reference}`
        + `&plan=${MIGRATION_MAP[bnSub.sku]}`

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })

      const result = await response.json()

      if (response.ok) {
        trackObjectClicked('migration_api_success')

        const migratedSku = MIGRATION_MAP[bnSub.sku];
        const migratedSkuArr = migratedSku ? migratedSku.split("_") : [];

        const planType = migratedSku
          ? migratedSkuArr[1] === "ESSENTIAL"
            ? "Essential"
            : migratedSkuArr[1] === "ADVANCED"
              ? "Advanced"
              : ""
          : "";

        Cookies.set("integoPlan", planType, {
          domain: ".intego.com",
          path: "/",
          sameSite: "Lax",
          expires: 2,
        });
        window.location.href = getRedirectionUrl()
      } else {
        throw new Error(result.message || 'Migration failed')
      }
    } catch (err) {
      trackObjectClicked('migration_api_failed', {reference_id: bnSub?.reference})

      toast.error(`Error: ${err?.message || err}. Please contact our support team for assistance.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    trackObjectClicked(TAB_TRACKING[activeTab].back)
  }

  const handleCheckboxChange = (e) => {
    if (e.target.checked) trackObjectClicked('final_step_check')
    setAgreed(e.target.checked)
  }

  return (
    <div className="migrate-page-wrap">
      <ToastContainer />
      <h3 className="migrate-heading">
        Welcome to <span className="migrate-heading-accent">Intego ONE</span>
      </h3>
      <div className="migrate-container">
        <div className="migrate-wrapper">

          <div className="migrate-image-mobile">
            <img src={heroMobile} alt="Intego ONE" />
          </div>
          <div className="migrate-image-desktop">
            <img src={heroImage} alt="Intego ONE" />
          </div>

          <div className="migrate-content">

            <div className="migrate-tabs">
              {TABS.map((tab) => {
                const isCompleted = activeTab > tab.id
                const isActive    = activeTab === tab.id
                const tabClass    = `migrate-tab-btn${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}`
                const barWidth    = isCompleted ? '100%' : isActive ? '50%' : '0%'
                return (
                  <div key={tab.id} className="migrate-tab-item">
                    <button
                      className={tabClass}
                      onClick={() => tab.id <= activeTab && setActiveTab(tab.id)}
                      disabled={tab.id > activeTab}
                    >
                      {isCompleted
                        ? <img src={blueTick} alt="" className="migrate-check-icon" />
                        : <span>{tab.id}.</span>
                      }
                      {tab.label.toUpperCase()}
                    </button>
                    <div className="migrate-tab-bar">
                      <div className="migrate-tab-bar-fill" style={{ width: barWidth }} />
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="migrate-step-content">
              {activeTab === 1 && <StepIntro />}
              {activeTab === 2 && <StepRequirements />}
              {activeTab === 3 && <StepNextStep checked={agreed} onCheck={(val) => {
                if (val) trackObjectClicked('final_step_check')
                setAgreed(val)
              }} />}
            </div>

            <div className={`migrate-buttons${activeTab === 3 ? ' step3' : ''}`}>
              {activeTab === 3 && (
                <label className="migrate-checkbox-inline">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={handleCheckboxChange}
                  />
                  <span>I have read and understand the changes above.</span>
                </label>
              )}
              <a className="migrate-back-btn" href="/welcome" onClick={handleBack}>
                Back to My Account
              </a>
              <div className={`migrate-btn-wrap ${stepClass}`}>
                <button onClick={handleContinue} disabled={continueDisabled}>
                  {isLoading ? 'Processing...' : label}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}

export default MigrateModal